import { useClinicDay } from '@hooks/useClinicDay'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AudioPlayer,
  Badge,
  DataTable,
  DataTableColumn,
  EmptyState,
  type BadgeColor,
  type DataTableRow,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { TableSkeleton } from '@components/ui'
import type { ICalls, ICallTag, ICallType } from '@dataTypes/calls.type'
import { fetchAiReceptionistActorApi } from '@services/api/ai-receptionist-settings.api'
import { fetchAllCallLogs, generatePresignedUrlsForCalls } from '@store/actions'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  formatCallDate,
  getCallTimestamp,
  getCallAiPlaceholder,
  isCallAiInProgress,
} from '@utils/call.util'
import { needsRecordingCanonicalize } from '@utils/call-recording-playback.util'
import { getFullName } from '@utils/fullname.util'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { AiReceptionistIcon } from '@components/common'
import { useCallRecordingObjectUrl } from '@hooks/useCallRecording'

type AiReceptionistCallRow = DataTableRow & {
  call_id: string
  call_type: ICallType
  _contact_name: string
  _contact_number: string
  _call_date: string
  _call_timestamp: number | null
  sentiments: string
  notes_count: string
}

const SENTIMENT_BADGE_COLOR: Record<string, BadgeColor> = {
  positive: 'pale-green',
  negative: 'red',
  neutral: 'gray',
}

const TAG_BADGE_COLOR: Record<string, BadgeColor> = {
  resolved: 'pale-green',
  feedback: 'blue',
  inquiry: 'cyan',
  'follow-up required': 'yellow',
  unresolved: 'orange',
  'high priority': 'red',
}

const formatSummaryText = (call: ICalls): string => {
  const summary = call.summary
    ?.split('\n')
    .map((line) => line.trim().replace(/^-+\s*/, ''))
    .filter(Boolean)
    .join(' ')
  if (summary) return summary
  if (isCallAiInProgress(call)) return 'Analyzing...'
  return 'No summary available.'
}

const TagsCell = ({ call }: { call: ICalls }): React.ReactElement => {
  const tags = call.tags ?? []
  if (!tags.length) {
    return (
      <span className="ds-type-paragraph-small">
        {getCallAiPlaceholder(call)}
      </span>
    )
  }

  return (
    <div className="ai-receptionist-calls__tags">
      {tags.map((tag: ICallTag) => (
        <Badge
          key={tag}
          label={tag}
          mode="tinted"
          color={TAG_BADGE_COLOR[tag.toLowerCase()] ?? 'gray'}
        />
      ))}
    </div>
  )
}

const SentimentCell = ({ call }: { call: ICalls }): React.ReactElement => {
  const sentiment = call.sentiments?.trim()
  if (!sentiment) {
    return (
      <span className="ds-type-paragraph-small">
        {getCallAiPlaceholder(call)}
      </span>
    )
  }

  return (
    <Badge
      label={sentiment}
      mode="tinted"
      color={SENTIMENT_BADGE_COLOR[sentiment.toLowerCase()] ?? 'gray'}
    />
  )
}

const CallExpandedContent = ({
  call,
  onNeedCanonicalize,
}: {
  call: ICalls
  onNeedCanonicalize: (call: ICalls) => void
}): React.ReactElement => {
  const liveRecordingUrl = useAppSelector(
    (state) =>
      state.calls.data?.find((c: ICalls) => c.call_id === call.call_id)
        ?.recording_url,
  )
  const recordingUrl = liveRecordingUrl ?? call.recording_url
  const playbackSrc = useCallRecordingObjectUrl(recordingUrl)
  const needsCanonicalize =
    Boolean(recordingUrl && call.call_sid) &&
    needsRecordingCanonicalize(recordingUrl)

  useEffect(() => {
    if (needsCanonicalize) {
      onNeedCanonicalize(call)
    }
  }, [call, needsCanonicalize, onNeedCanonicalize])

  return (
    <div className="ai-receptionist-calls__expand">
      {recordingUrl ? (
        <AudioPlayer
          src={playbackSrc || undefined}
          disabled={!playbackSrc}
          className="ai-receptionist-calls__audio"
        />
      ) : null}
      <div className="ai-receptionist-calls__summary">
        <span className="ai-receptionist-calls__summary-label">AI summary</span>
        <span className="ai-receptionist-calls__summary-text">
          {formatSummaryText(call)}
        </span>
      </div>
    </div>
  )
}

const AiReceptionistCallsTab = ({
  searchQuery,
  selectedFilters,
}: {
  searchQuery: string
  selectedFilters: FilterSelection
}): React.ReactElement => {
  const dispatch = useAppDispatch()
  const clinicTimeZone = useAppSelector(
    (state) => state.clinic.selectedClinic?.time_zone || 'UTC',
  )
  const clinicDay = useClinicDay(clinicTimeZone)
  const clinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id ?? '',
  )
  const callLogs = useAppSelector((state) => state.calls.data ?? [])
  const callsLoading = useAppSelector((state) => state.calls.loading)

  const [actorId, setActorId] = useState<string>('')
  const [actorLoading, setActorLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])
  const [clinicPending, setClinicPending] = useState(true)
  const [trackedClinicId, setTrackedClinicId] = useState(clinicId)
  if (trackedClinicId !== clinicId) {
    setTrackedClinicId(clinicId)
    setActorId('')
    setActorLoading(true)
    setClinicPending(true)
    setError('')
  }

  useEffect(() => {
    let cancelled = false
    setActorLoading(true)
    setClinicPending(true)
    void dispatch(fetchAllCallLogs())
    void fetchAiReceptionistActorApi()
      .then((response) => {
        if (!cancelled) {
          setActorId(response.data.user_id || '')
          setActorLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('AI receptionist calls could not be loaded.')
          setActorLoading(false)
        }
      })
      .finally(() => {
        if (!cancelled) setClinicPending(false)
      })
    return () => {
      cancelled = true
    }
  }, [clinicId, dispatch])

  const handleNeedCanonicalize = useCallback(
    (call: ICalls) => {
      if (call.call_id && call.call_sid) {
        void dispatch(generatePresignedUrlsForCalls([call]))
      }
    },
    [dispatch],
  )

  const filteredCalls = useMemo(() => {
    if (!actorId) return []
    return callLogs.filter((call) => call.handled_by_user_id === actorId)
  }, [actorId, callLogs])

  const callsById = useMemo(() => {
    const map = new Map<string, ICalls>()
    filteredCalls.forEach((call) => map.set(call.call_id, call))
    return map
  }, [filteredCalls])

  const rows: AiReceptionistCallRow[] = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const selectedTypes = new Set(
      (selectedFilters.type ?? []).map((value) => value.toLowerCase()),
    )
    const selectedSentiments = new Set(
      (selectedFilters.sentiment ?? []).map((value) => value.toLowerCase()),
    )

    return filteredCalls
      .filter((call) => {
        const contactNumber = ['Outgoing', 'Decline'].includes(call.call_type)
          ? call.receiver_number
          : call.caller_number
        const contactName = call.contact
          ? getFullName(call.contact)
          : contactNumber || ''
        if (query) {
          const haystack = `${contactName} ${contactNumber ?? ''}`.toLowerCase()
          if (!haystack.includes(query)) return false
        }
        if (
          selectedTypes.size > 0 &&
          !selectedTypes.has(call.call_type.toLowerCase())
        ) {
          return false
        }
        if (selectedSentiments.size > 0) {
          const sentiment = (call.sentiments ?? '').toLowerCase()
          if (!selectedSentiments.has(sentiment)) return false
        }
        return true
      })
      .map((call) => {
        const contactNumber = ['Outgoing', 'Decline'].includes(call.call_type)
          ? call.receiver_number
          : call.caller_number
        const contactName = call.contact
          ? getFullName(call.contact)
          : contactNumber || '—'

        return {
          id: call.call_id,
          call_id: call.call_id,
          call_type: call.call_type,
          _contact_name:
            contactName === 'N/A' ? contactNumber || '—' : contactName,
          _contact_number:
            formatPhoneNumber(contactNumber) || contactNumber || '—',
          _call_date: formatCallDate(call.start_date_time, clinicTimeZone),
          _call_timestamp: getCallTimestamp(call.start_date_time),
          sentiments: call.sentiments ?? '',
          notes_count: call.call_notes?.length
            ? String(call.call_notes.length)
            : '—',
        }
      })
  }, [filteredCalls, searchQuery, selectedFilters, clinicTimeZone, clinicDay])

  const columns: DataTableColumn<AiReceptionistCallRow>[] = useMemo(
    () => [
      {
        id: '_contact_name',
        label: 'Contact name',
        sortable: true,
        renderCell: (row) => (
          <span className="ai-receptionist-name">
            <span className="ai-receptionist-name__text">
              {String(row._contact_name ?? '')}
            </span>
            <AiReceptionistIcon label="Assisted by AI receptionist" />
          </span>
        ),
      },
      { id: '_contact_number', label: 'Contact number', sortable: true },
      {
        id: '_call_timestamp',
        renderCell: (row) => row._call_date,
        label: 'Call date',
        sortable: true,
      },
      {
        id: 'tags',
        label: 'Tags',
        sortable: false,
        renderCell: (row) => {
          const call = callsById.get(row.call_id)
          return call ? <TagsCell call={call} /> : '—'
        },
      },
      {
        id: 'sentiments',
        label: 'Sentiment',
        sortable: true,
        renderCell: (row) => {
          const call = callsById.get(row.call_id)
          return call ? <SentimentCell call={call} /> : '—'
        },
      },
      {
        id: 'notes_count',
        label: 'Notes',
        sortable: false,
        align: 'center',
      },
    ],
    [callsById],
  )

  const loading = actorLoading || callsLoading

  if (error) {
    return (
      <EmptyState
        className="ai-receptionist-calls__empty"
        illustration={<CallIcon className="empty-icon" aria-hidden />}
        title="Unable to load calls"
        description={error}
      />
    )
  }

  if (clinicPending || loading) {
    return (
      <TableSkeleton
        className="ai-receptionist-calls__table"
        columns={[
          { width: '14%', kind: 'text' },
          { width: '14%', kind: 'text' },
          { width: '16%', kind: 'text' },
          { width: '16%', kind: 'badge' },
          { width: '12%', kind: 'badge' },
          { width: '8%', kind: 'text' },
        ]}
        rows={3}
        showToolbar={false}
        showPagination={false}
        fillHeight={false}
      />
    )
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        className="ai-receptionist-calls__empty"
        illustration={<CallIcon className="empty-icon" aria-hidden />}
        title="No calls found"
        description={
          searchQuery.trim() ||
          (selectedFilters.type ?? []).length > 0 ||
          (selectedFilters.sentiment ?? []).length > 0
            ? 'No calls match your search.'
            : 'No calls have been handled by the AI receptionist yet.'
        }
      />
    )
  }

  return (
    <DataTable
      className="ai-receptionist-calls__table"
      columns={columns}
      rows={rows}
      showActions={false}
      expandable
      expandableColumnId="_contact_name"
      expandedRowIds={expandedRowIds}
      onExpandedRowIdsChange={setExpandedRowIds}
      renderExpandedContent={(row) => {
        const call = callsById.get(String(row.call_id ?? row.id))
        if (!call) return null
        return (
          <CallExpandedContent
            call={call}
            onNeedCanonicalize={handleNeedCanonicalize}
          />
        )
      }}
    />
  )
}

export default AiReceptionistCallsTab
